import React, { useState, useMemo, useEffect } from "react";
import capitalize from "lodash/capitalize";

import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";

import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  TextField,
  Grid,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Select,
  MenuItem,
  TableRow,
  TableCell,
  FormControl,
} from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import Dialog from "src/components/Dialog";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";

import { toast } from "react-toastify";

import InputAdornment from "@material-ui/core/InputAdornment";

import {
  userIncomeTaxRateSelector,
  userPlanSelector,
  expenseRulesSelector,
  scheduleCMappingsSelector,
  isImpersonationSessionSelector,
} from "src/store/system/selector";

import {
  userGeneralLedgerAccountsSelector,
  generalLedgerAccountsSelector,
} from "src/store/glAccounts/selector";
import { bankAccountsSelector } from "src/store/bankAccount/selector";
import { scheduleCAccountsSelector } from "src/pages/ReportsPage/ScheduleCReport/selector";
import { getVendors } from "src/store/vendor/selector";
import { setActiveDialog } from "src/store/adminHtml/actions";
import {
  deleteGeneralLedgerAccount,
  updateGeneralLedgerAccount,
} from "src/apiService/modules/generalLedgerAccounts";
import { deleteTransactionRule } from "src/apiService/modules/transactionRules";
import { deleteVendor } from "src/apiService/modules/vendors";
import { getSubscription } from "src/apiService/modules/stripe";
import LoadableButton, {
  StatefulLoadableButton,
} from "src/components/LoadableButton";
import { updateUserApi } from "src/apiService/modules/system";
import {
  resetUserOnboarding,
  updateScheduleCMappings,
} from "src/apiService/modules/users";
import { ScheduleCLineOptions } from "src/pages/ReportsPage/ScheduleCReport/MappingsDialog";
import type { ExpenseType } from "src/interfaces/systemState.interface";
import { userRoleSelector } from "src/store/system/selector";
import { GeneralLedgerAccountTypeToLabelMaps } from "src/utils/general-ledger-accounts";

import InventoryAccountingMethodSelect from "./InventoryAccountingMethodSelect";
import RestartButton from "./RestartButton";
import DeleteButton from "./DeleteButton";
import AnalyticsGroupings from "./AnalyticsGroupings";
import VendorDialog from "./VendorDialog";
import ManageSubscriptionButton from "./ManageSubscriptionButton";
import WalkThroughTopicsList from "./WalkThroughTopicsList";
import AccountantAccess from "./AccountantAccess";
import CancellationModal from "../../../components/CancellationSurvey/CancellationModal";
import CompanyInfo from "./CompanyInfo";
import { useRecordEvent } from "src/hooks/useRecordEvent";
import { useHistory } from "react-router-dom";
import AppearanceSettings from "./AppearanceSettings";
import ProfileSettingsTable, {
  sortRowsByKey,
  useProfileSettingsTableInputStyles,
} from "./ProfileSettingsTable";
import VendorNameCell from "./VendorNameCell";

const useEstimatedIncomeTaxRowStyles = makeStyles((theme) => ({
  button: {
    margin: theme.spacing(1, 0, "auto", 1),
    height: 40,
  },
}));

function EstimatedIncomeTaxRow() {
  const classes = useEstimatedIncomeTaxRowStyles();
  const incomeTaxRate = useSelector(userIncomeTaxRateSelector);
  const userRole = useSelector(userRoleSelector);
  const formik = useFormik({
    initialValues: {
      incomeTaxRate: Number(incomeTaxRate) * 100 || 0,
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (
        values.incomeTaxRate === undefined ||
        values.incomeTaxRate < 0 ||
        values.incomeTaxRate > 100
      ) {
        errors.incomeTaxRate = "Must be 0 ~ 100";
      }

      return errors;
    },
    onSubmit: async (values) => {
      if (!values.incomeTaxRate) return;
      if (
        values.incomeTaxRate === undefined ||
        values.incomeTaxRate < 0 ||
        values.incomeTaxRate > 100
      )
        return;
      try {
        await updateUserApi({
          income_tax_rate: values.incomeTaxRate / 100,
        });
        toast.success("Saved successfully!");
      } catch (e) {
        toast.error(`There was an error: ${(e as Error).toString()}`);
      }
    },
  });

  return (
    <Box
      display="flex"
      component="form"
      {...({ onSubmit: formik.handleSubmit } as any)}
    >
      <TextField
        fullWidth
        error={!!formik.errors.incomeTaxRate}
        helperText={formik.errors.incomeTaxRate}
        variant="outlined"
        margin="dense"
        label="Estimated income tax rate"
        type="number"
        value={formik.values.incomeTaxRate}
        disabled={formik.isSubmitting}
        name="incomeTaxRate"
        onChange={formik.handleChange}
        InputProps={{
          endAdornment: <InputAdornment position="end">%</InputAdornment>,
          readOnly: userRole === "viewer",
        }}
      />
      <LoadableButton
        className={classes.button}
        type="submit"
        color="primary"
        variant="contained"
        loading={formik.isSubmitting}
        disabled={
          Object.values(formik.errors).length > 0 || userRole === "viewer"
        }
      >
        Save
      </LoadableButton>
    </Box>
  );
}

function renewalDateToString(date: Date | undefined): string {
  if (!date) return "--/--/----";
  return date.toLocaleDateString("en-US");
}

interface StripeSubscriptionResponse {
  loading?: boolean;
  error?: string;
  subscription?: {
    current_period_end: Date;
  };
}

function useStripeSubscription(): StripeSubscriptionResponse {
  const isImpersonation = useSelector(isImpersonationSessionSelector);
  const [subscription, setSubscription] = useState<StripeSubscriptionResponse>({
    loading: !isImpersonation,
  });
  useEffect(() => {
    if (isImpersonation) {
      setSubscription({});
      return;
    }
    let cancel = false;
    (async () => {
      try {
        const data = await getSubscription();
        if (cancel) return;
        if (data.error) {
          setSubscription({ error: data.error });
        } else {
          setSubscription({
            subscription: {
              ...data,
              current_period_end: new Date(data.current_period_end * 1000),
            },
          });
        }
      } catch (e) {
        setSubscription({
          error: (e as Error).message || (e as Error).toString(),
        });
      }
    })();
    return () => {
      cancel = true;
    };
  }, [isImpersonation]);
  return subscription;
}

const useStyles = makeStyles((theme) => ({
  bold: {
    fontWeight: theme.typography.fontWeightBold,
  },
  separate: {
    marginTop: theme.spacing(2),
  },
  profileActionButtons: {
    width: "75%",
  },
  errorButton: {
    width: "75%",
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  },
  vendorRow: {
    cursor: "pointer",
  },
  vendorBooleanIcon: {
    display: "block",
    margin: "0 auto",
  },
}));

function ProfileSettingsDialogContent() {
  const recordEvent = useRecordEvent();

  const classes = useStyles();
  const tableInputClasses = useProfileSettingsTableInputStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const _glAccounts = useSelector(userGeneralLedgerAccountsSelector);
  const glAccounts = useMemo(() => {
    const accounts = _glAccounts.filter((gl) => !gl.deleted);
    accounts.sort((a, b) => a.name.localeCompare(b.name));
    return accounts;
  }, [_glAccounts]);
  const expenseRules = useSelector(expenseRulesSelector);
  const sortedExpenseRules = useMemo(
    () => sortRowsByKey(expenseRules, (rule) => rule.plaid_description || ""),
    [expenseRules]
  );
  const vendors = useSelector(getVendors);
  const sortedVendors = useMemo(
    () => sortRowsByKey(vendors, (vendor) => vendor.name || ""),
    [vendors]
  );
  const [vendor, setVendor] = React.useState<string | undefined>(undefined);
  const [isCancellationSurveyOpen, setIsCancellationSurveyOpen] =
    useState(false);
  const plan = useSelector(userPlanSelector) || "basic";
  const subscription = useStripeSubscription();
  const allGeneralLedgerAccounts = useSelector(scheduleCAccountsSelector);
  const sortedAllGeneralLedgerAccounts = useMemo(() => {
    const gl = [...allGeneralLedgerAccounts];
    gl.sort((a, b) => a.name.localeCompare(b.name));
    return gl;
  }, [allGeneralLedgerAccounts]);
  const scheduleCMappings = useSelector(scheduleCMappingsSelector);
  const userRole = useSelector(userRoleSelector);
  const isImpersonation = useSelector(isImpersonationSessionSelector);
  const generalLedgerAccounts = useSelector(generalLedgerAccountsSelector);
  const bankAccounts = useSelector(bankAccountsSelector);
  const glAccountBankTypeMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const ba of bankAccounts) {
      if (!ba.general_ledger_account_id) continue;
      map.set(ba.general_ledger_account_id, ba.type);
    }
    return map;
  }, [bankAccounts]);
  const isViewer = userRole === "viewer";
  // Owner-only billing actions; also blocked for any impersonation session.
  const billingDisabled = isViewer || isImpersonation;

  const updateLedgerRow = (id, value, field) => {
    updateGeneralLedgerAccount(id, value, field).then((_) => {
      console.log("Ledger account updated.");
    });
  };

  const deleteLedgerAccount = (id) => {
    return deleteGeneralLedgerAccount(id).then((_) => {
      console.log("Ledger account deleted.");
    });
  };

  const handleGlTypeChange = (accountId: string, event) => {
    const value = (event.target.value as string).split("/");
    const bankType = glAccountBankTypeMap.get(accountId);
    if (bankType) {
      const newType = value.join("/");
      if (bankType === "sale-platform") {
        toast.error(
          "This account is linked to a sale platform and its type cannot be changed."
        );
        return;
      }
      if (
        bankType === "bank-account" &&
        newType !== "assets/bank-account-cash" &&
        newType !== "equity/owner-contributions-draws"
      ) {
        toast.error(
          "Bank accounts can only be set to Bank Account/Cash or Owner Contributions/Draws."
        );
        return;
      }
      if (
        bankType === "credit-card" &&
        newType !== "liabilities/credit-card" &&
        newType !== "equity/owner-contributions-draws"
      ) {
        toast.error(
          "Credit card accounts can only be set to Credit Card or Owner Contributions/Draws."
        );
        return;
      }
    }
    updateLedgerRow(accountId, value, "type");
  };

  return (
    <>
      <VendorDialog id={vendor} onClose={() => setVendor(undefined)} />
      <CancellationModal
        isOpen={isCancellationSurveyOpen}
        onClose={() => {
          setIsCancellationSurveyOpen(false);
        }}
      />
      <DialogTitle>Profile settings</DialogTitle>
      <DialogContent>
        <DialogContentText className="relative" tabIndex={-1} component="div">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography className={classes.bold}>Company</Typography>
            </Grid>
            <Grid item xs={12}>
              <CompanyInfo />
            </Grid>
            <Grid item xs={12} className={classes.separate}>
              <Typography className={classes.bold}>Appearance</Typography>
            </Grid>
            <Grid item xs={12}>
              <AppearanceSettings />
            </Grid>
            <Grid item xs={6} className={classes.separate}>
              <TextField
                margin="dense"
                variant="outlined"
                label="Subscription"
                value={capitalize(plan)}
                fullWidth
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={6} className={classes.separate}>
              <Box position="relative">
                <TextField
                  disabled={!!subscription.loading}
                  error={!!subscription.error}
                  margin="dense"
                  variant="outlined"
                  label="Renewal Date"
                  value={renewalDateToString(
                    subscription.subscription?.current_period_end
                  )}
                  helperText={subscription.error}
                  fullWidth
                  InputProps={{
                    readOnly: true,
                  }}
                />
                {subscription.loading ? (
                  <Box
                    position="absolute"
                    display="flex"
                    top="50%"
                    left="50%"
                    mt={-1.5}
                    ml={-1.5}
                  >
                    <CircularProgress size={24} />
                  </Box>
                ) : null}
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box display="flex" justifyContent="center" width="100%">
                <ManageSubscriptionButton
                  className={classes.profileActionButtons}
                  disabled={billingDisabled}
                  size="large"
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box display="flex" justifyContent="center">
                <Button
                  size="large"
                  variant="contained"
                  onClick={async () => {
                    setIsCancellationSurveyOpen(true);
                  }}
                  className={classes.errorButton}
                  disabled={billingDisabled}
                >
                  Cancel Subscription
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} className={classes.separate}>
              <InventoryAccountingMethodSelect
                disabled={userRole === "viewer"}
              />
            </Grid>
            <Grid item xs={12} className={classes.separate}>
              <EstimatedIncomeTaxRow />
            </Grid>
            <AccountantAccess className={classes.separate} />
            <Grid item xs={12} className={classes.separate}>
              <WalkThroughTopicsList />
            </Grid>
            <AnalyticsGroupings className={classes.separate} />
            <Grid item xs={12} className={classes.separate}>
              <Typography className={classes.bold}>Expense Rules</Typography>
            </Grid>
            <Grid item xs={12}>
              <ProfileSettingsTable
                columns={[
                  { id: "charge", label: "Charge Description", width: "22%" },
                  {
                    id: "expense",
                    label: "Expense Description",
                    width: "28%",
                  },
                  {
                    id: "account",
                    label: "General Ledger Account",
                    width: "22%",
                  },
                  { id: "vendor", label: "Vendor", width: "18%" },
                  { id: "remove", label: "Remove", width: 88, align: "center" },
                ]}
              >
                {sortedExpenseRules.map((rule) => (
                  <TableRow
                    key={`${rule.plaid_description}-${rule.description}-${rule.account}`}
                  >
                    <TableCell className={tableInputClasses.tableCellDense}>
                      {rule.plaid_description}
                    </TableCell>
                    <TableCell className={tableInputClasses.tableCellDense}>
                      {rule.description}
                    </TableCell>
                    <TableCell className={tableInputClasses.tableCellDense}>
                      {rule.account}
                    </TableCell>
                    <TableCell className={tableInputClasses.tableCellDense}>
                      <VendorNameCell vendorId={rule.vendor} />
                    </TableCell>
                    <TableCell
                      className={tableInputClasses.tableCellDense}
                      align="center"
                    >
                      <DeleteButton
                        disabled={isViewer}
                        onClick={async () =>
                          await deleteTransactionRule(rule.plaid_description)
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </ProfileSettingsTable>
            </Grid>
            <Grid item xs={12} className={classes.separate}>
              <Typography className={classes.bold}>
                General Ledger Accounts
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <ProfileSettingsTable
                columns={[
                  { id: "name", label: "Account Name", width: "45%" },
                  { id: "type", label: "Type", width: "40%" },
                  { id: "remove", label: "Remove", width: 88, align: "center" },
                ]}
              >
                {glAccounts.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell className={tableInputClasses.tableCellDense}>
                      <TextField
                        key={`${account.id}-${account.name}`}
                        fullWidth
                        variant="outlined"
                        margin="none"
                        size="small"
                        className={tableInputClasses.tableTextField}
                        defaultValue={account.name}
                        disabled={isViewer}
                        onBlur={(event) => {
                          const value = event.target.value.trim();
                          if (value && value !== account.name) {
                            updateLedgerRow(account.id, value, "name");
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell className={tableInputClasses.tableCellDense}>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        size="small"
                        margin="dense"
                        className={tableInputClasses.tableFormControl}
                        disabled={isViewer}
                      >
                        <Select
                          native
                          value={account.type?.join?.("/") || ""}
                          onChange={(event) =>
                            handleGlTypeChange(account.id, event)
                          }
                          inputProps={{ "aria-label": "Account Type" }}
                        >
                          {Object.entries(
                            GeneralLedgerAccountTypeToLabelMaps
                          ).map(([key, value]) => {
                            const types = Object.entries(value.types);
                            types.sort((a, b) => a[1].localeCompare(b[1]));
                            return (
                              <optgroup key={key} label={value.label}>
                                {types.map(([k, v]) => (
                                  <option
                                    key={`${key}/${k}`}
                                    value={`${key}/${k}`}
                                  >
                                    {v}
                                  </option>
                                ))}
                              </optgroup>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell
                      className={tableInputClasses.tableCellDense}
                      align="center"
                    >
                      <DeleteButton
                        disabled={isViewer}
                        onClick={async () =>
                          await deleteLedgerAccount(account.id)
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </ProfileSettingsTable>
            </Grid>
            <Grid item xs={12}>
              <Button
                onClick={(_) => {
                  dispatch(setActiveDialog("add_gl_account"));
                }}
                variant="contained"
                color="primary"
                disabled={isViewer}
              >
                Add
              </Button>
            </Grid>
            <Grid item xs={12} className={classes.separate}>
              <Typography className={classes.bold}>Vendors</Typography>
            </Grid>
            <Grid item xs={12}>
              <ProfileSettingsTable
                columns={[
                  { id: "name", label: "Vendor Name", width: "40%" },
                  {
                    id: "contractor",
                    label: "1099 Contractor",
                    width: "22%",
                    align: "center",
                  },
                  {
                    id: "consignor",
                    label: "Consignor",
                    width: "22%",
                    align: "center",
                  },
                  { id: "remove", label: "Remove", width: 88, align: "center" },
                ]}
              >
                {sortedVendors.map((vendorRow) => (
                  <TableRow
                    key={vendorRow.id}
                    className={!isViewer ? classes.vendorRow : undefined}
                    onDoubleClick={
                      isViewer ? undefined : () => setVendor(`${vendorRow.id}`)
                    }
                  >
                    <TableCell className={tableInputClasses.tableCellDense}>
                      {vendorRow.name}
                    </TableCell>
                    <TableCell
                      className={tableInputClasses.tableCellDense}
                      align="center"
                    >
                      {vendorRow.contractor ? (
                        <CheckIcon
                          className={classes.vendorBooleanIcon}
                          fontSize="small"
                          color="primary"
                        />
                      ) : (
                        <CloseIcon
                          className={classes.vendorBooleanIcon}
                          fontSize="small"
                          color="disabled"
                        />
                      )}
                    </TableCell>
                    <TableCell
                      className={tableInputClasses.tableCellDense}
                      align="center"
                    >
                      {vendorRow.consignor ? (
                        <CheckIcon
                          className={classes.vendorBooleanIcon}
                          fontSize="small"
                          color="primary"
                        />
                      ) : (
                        <CloseIcon
                          className={classes.vendorBooleanIcon}
                          fontSize="small"
                          color="disabled"
                        />
                      )}
                    </TableCell>
                    <TableCell
                      className={tableInputClasses.tableCellDense}
                      align="center"
                    >
                      <DeleteButton
                        disabled={isViewer}
                        onClick={async () => {
                          try {
                            const ret = await deleteVendor(vendorRow.id);
                            if (ret.error)
                              toast.error(
                                `This vendor can't be deleted since there are ${
                                  Array.isArray(ret.error)
                                    ? ret.error.join("/")
                                    : "inventory/sales/expenses"
                                } with the vendor assigned to them. Remove the vendor from those transactions to delete the vendor.`
                              );
                          } catch (e) {
                            toast.error(`Error: ${(e as Error).toString()}`);
                          }
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </ProfileSettingsTable>
            </Grid>
            <Grid item xs={12}>
              <Button
                onClick={() => {
                  setVendor("add-new-vendor");
                }}
                variant="contained"
                color="primary"
                disabled={isViewer}
              >
                Add
              </Button>
            </Grid>
            <Grid item xs={12} className={classes.separate}>
              <Typography className={classes.bold}>Schedule C</Typography>
            </Grid>
            <Grid item xs={12}>
              <ProfileSettingsTable
                columns={[
                  { id: "account", label: "MRG Account", width: "40%" },
                  { id: "mapping", label: "Schedule C Line", width: "60%" },
                ]}
              >
                {sortedAllGeneralLedgerAccounts.map((account) => (
                  <TableRow key={account.id || account.name}>
                    <TableCell className={tableInputClasses.tableCellDense}>
                      {account.name}
                    </TableCell>
                    <TableCell className={tableInputClasses.tableCellDense}>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        size="small"
                        margin="dense"
                        className={tableInputClasses.tableFormControl}
                        disabled={isViewer}
                      >
                        <Select
                          displayEmpty
                          value={scheduleCMappings[account.id] || ""}
                          onChange={async (event) => {
                            await updateScheduleCMappings(
                              {
                                [account.id as string]:
                                  (event.target.value as ExpenseType) ||
                                  undefined,
                              },
                              {},
                              { generalLedgerAccounts }
                            );
                          }}
                        >
                          <MenuItem dense value="">
                            None
                          </MenuItem>
                          {ScheduleCLineOptions.map((opt) => (
                            <MenuItem dense value={opt.entry} key={opt.entry}>
                              {opt.text}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                ))}
              </ProfileSettingsTable>
            </Grid>
            <Grid item xs={12} className={classes.separate}>
              <Typography component="p" variant="subtitle1" align="center">
                Want to restart set up? Click the button below to keep your
                current data and go through the guided set up again.
              </Typography>
              <Box display="flex" width="100%" justifyContent="center" my={2}>
                <StatefulLoadableButton
                  variant="contained"
                  color="primary"
                  disabled={isViewer}
                  onClick={async () => {
                    recordEvent({
                      type: "feature-adoption",
                      feature: "show-set-up",
                    });
                    await resetUserOnboarding();
                    history.push("/");
                  }}
                >
                  Show Set Up
                </StatefulLoadableButton>
              </Box>
            </Grid>
            <Grid item xs={12} className={classes.separate}>
              <RestartButton />
            </Grid>
          </Grid>
        </DialogContentText>
      </DialogContent>
    </>
  );
}

interface ProfileSettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

function ProfileSettingsDialog({ open, onClose }: ProfileSettingsDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll={"body"}
      fullWidth={true}
      maxWidth={"md"}
    >
      <ProfileSettingsDialogContent />
    </Dialog>
  );
}

export default ProfileSettingsDialog;
